import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PortalType } from '@ats/api/cm';
import { filterNullOrUndefined } from '@ats/utility';
import { LocalizationService, LocalizationVM } from '@cm/core/localization';
import { SettingsService } from '@cm/core/settings';
import { SafeHtmlPipe } from '@cm/shared/ui/utilities';
import { map, of, switchMap } from 'rxjs';
import { LocalizationViewService } from '../../services/localization-view.service';
import { InliningLocalizationComponent } from '../inlining-localization/inlining-localization.component';

type CustomStyle = {
	[key: string]: string;
};

@Component({
	selector: 'cm-localize',
	templateUrl: './localize.component.html',
	styleUrls: ['./localize.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule, InliningLocalizationComponent, SafeHtmlPipe],
})
export class LocalizeComponent {
	readonly name = input.required<string>();
	readonly cpName = input<string | undefined>();
	readonly hideOn = input<'never' | 'emptyString' | 'null'>('never');
	readonly sanitize = input<boolean>(true);
	readonly customStyle = input<CustomStyle | undefined>();
	readonly customInliningStyle = input<CustomStyle | undefined>();

	private readonly portalSensitiveName = computed<string>(() => {
		if (this.settingsService.portal === PortalType.CandidatePortal && this.cpName()) {
			return this.cpName();
		}
		return this.name();
	});

	readonly loc = toSignal<(LocalizationVM & { safeHtml?: SafeHtml }) | null>(
		toObservable<string>(this.portalSensitiveName).pipe(
			switchMap(locName => (!!locName ? this.localizationService.find$(locName) : of(null))),
			filterNullOrUndefined(),
			map(loc =>
				!this.sanitize && loc?.htmlAllowed
					? { ...loc, safeHtml: this.sanitizer.bypassSecurityTrustHtml(loc.value || '') }
					: loc
			)
		)
	);

	readonly inlineEnabled$ = this.localizationViewService.inlineEnabled$;

	constructor(
		private readonly localizationService: LocalizationService,
		private readonly localizationViewService: LocalizationViewService,
		private readonly sanitizer: DomSanitizer,
		private readonly settingsService: SettingsService
	) {}
}
